import { useEffect, useState } from 'react';
import { companyApi, type CompanySettings } from '@/api/companyApi';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';

export function ShopDetailsCard() {
  const [company, setCompany] = useState<CompanySettings | null>(null);
  const billingStatus = useAuthStore((s) => s.billingStatus);
  const tenantDisplayName = useTenantStore((s) => s.displayName);
  const tenantCompanyName = useTenantStore((s) => s.companyName);

  useEffect(() => {
    companyApi
      .getSettings()
      .then(setCompany)
      .catch((err) => {
        console.error('Failed to load company settings:', err);
      });
  }, []);

  const shopName = tenantDisplayName || tenantCompanyName || 'Shop';

  const billingBadgeColor = {
    Trial: 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
    Active: 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400',
    PastDue: 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400',
    Cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400',
  };

  const badgeClass = billingBadgeColor[billingStatus as keyof typeof billingBadgeColor] || billingBadgeColor.Active;

  const address = company
    ? [company.address, company.city, company.province].filter(Boolean).join(', ')
    : '';

  return (
    <div className="p-6 space-y-4 border-b border-slate-200 dark:border-border-dark bg-white dark:bg-surface-dark">
      <div className="space-y-4">
        {/* Header: Shop Name + Billing Status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-base text-slate-900 dark:text-white line-clamp-2">
              {shopName}
            </h2>
          </div>
          {billingStatus && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg flex-shrink-0 whitespace-nowrap ${badgeClass}`}>
              {billingStatus}
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-slate-200 dark:bg-border-dark" />

        {/* Address */}
        <div className="flex items-start gap-3">
          <span className="material-symbols-outlined text-slate-400 text-lg flex-shrink-0 mt-0.5">location_on</span>
          <div className="flex-1 min-w-0">
            {address ? (
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-tight break-words">
                {address}
              </p>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">No address on file</p>
            )}
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-slate-400 text-lg flex-shrink-0">phone</span>
          {company?.phone ? (
            <a
              href={`tel:${company.phone}`}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline truncate"
            >
              {company.phone}
            </a>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">No phone</p>
          )}
        </div>

        {/* Email */}
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-slate-400 text-lg flex-shrink-0">mail</span>
          {company?.email ? (
            <a
              href={`mailto:${company.email}`}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline truncate"
            >
              {company.email}
            </a>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">No email</p>
          )}
        </div>
      </div>
    </div>
  );
}
